import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import WhatsAppFab from "@/components/WhatsAppFab";
import { ArrowRight, Sparkle } from "@phosphor-icons/react";

export default function Register() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const next = params.get("next");
    const planParam = params.get("plan");
    const [name, setName] = useState("");
    const [apellidos, setApellidos] = useState("");
    const [telefono, setTelefono] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [tipoCliente, setTipoCliente] = useState("empresa");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setError("");
        if (password.length < 6) {
            setError("La contraseña debe tener al menos 6 caracteres");
            return;
        }
        const phoneDigits = telefono.replace(/\D/g, "").replace(/^34/, "");
        if (phoneDigits.length < 9) {
            setError("Introduce un teléfono de contacto válido (mínimo 9 dígitos)");
            return;
        }
        setLoading(true);
        const r = await register({ name, apellidos, telefono, email, password, tipoCliente });
        setLoading(false);
        if (r.ok) {
            // Redirigir al destino indicado (ej: pagos con plan pendiente) o al dashboard
            if (next) {
                const sep = next.includes("?") ? "&" : "?";
                nav(planParam ? `${next}${sep}plan=${encodeURIComponent(planParam)}` : next);
            } else {
                nav("/dashboard");
            }
        }
        else setError(r.error);
    }

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />
            <div className="flex-1 flex items-center justify-center px-6 py-16">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
                    <div className="gf-card">
                        <div className="micro-label mb-2">— Registro</div>
                        <h1 className="serif text-3xl mb-2">Crea tu cuenta</h1>
                        <p className="text-sm text-[var(--gf-text-secondary)] mb-8" data-testid="register-welcome-credits">
                            Recibe <strong className="text-[var(--gf-navy)]">50 créditos de bienvenida</strong> (1 € = 1 crédito). Sin tarjeta.
                        </p>

                        {planParam && (
                            <div className="mb-6 p-4 border-l-4 border-l-[var(--gf-gold)] bg-[var(--gf-gold)]/10 flex items-start gap-3" data-testid="register-plan-banner">
                                <Sparkle size={20} weight="fill" className="text-[var(--gf-gold)] flex-shrink-0 mt-0.5" />
                                <div className="text-sm">
                                    <strong>Plan seleccionado: {planLabel(planParam)}.</strong>
                                    <br />
                                    Crea tu cuenta y te llevamos automáticamente al pago seguro de Stripe.
                                </div>
                            </div>
                        )}

                        <form onSubmit={submit} className="space-y-5" data-testid="register-form">
                            <div>
                                <label className="gf-label">¿Con qué perfil usarás la app?</label>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="register-tipo-group">
                                    {[
                                        { v: "empresa", t: "Empresa", d: "Una o varias empresas propias" },
                                        { v: "autonomo", t: "Autónomo", d: "Facturación personal" },
                                        { v: "asesoria", t: "Asesoría", d: "Gestiono clientes de terceros" },
                                    ].map((o) => (
                                        <label
                                            key={o.v}
                                            className={`cursor-pointer border p-3 text-sm transition-all ${tipoCliente === o.v ? "border-[var(--gf-gold)] bg-[var(--gf-gold)]/10" : "border-[var(--gf-border)] hover:border-[var(--gf-text-secondary)]"}`}
                                            data-testid={`register-tipo-${o.v}`}
                                        >
                                            <input type="radio" name="tipoCliente" value={o.v} checked={tipoCliente === o.v} onChange={(e) => setTipoCliente(e.target.value)} className="sr-only" />
                                            <div className="font-semibold text-[var(--gf-navy)]">{o.t}</div>
                                            <div className="text-xs text-[var(--gf-text-secondary)] mt-1">{o.d}</div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="gf-label">Nombre</label>
                                    <input type="text" required className="gf-input" value={name}
                                        onChange={(e) => setName(e.target.value)} data-testid="register-name" />
                                </div>
                                <div>
                                    <label className="gf-label">Apellidos</label>
                                    <input type="text" required className="gf-input" value={apellidos}
                                        onChange={(e) => setApellidos(e.target.value)} data-testid="register-apellidos" />
                                </div>
                            </div>
                            <div>
                                <label className="gf-label">Teléfono</label>
                                <input type="tel" required className="gf-input" value={telefono} placeholder="Ej: 600 123 456"
                                    onChange={(e) => setTelefono(e.target.value)} data-testid="register-telefono" />
                            </div>
                            <div>
                                <label className="gf-label">Email</label>
                                <input type="email" required className="gf-input" value={email}
                                    onChange={(e) => setEmail(e.target.value)} data-testid="register-email" />
                            </div>
                            <div>
                                <label className="gf-label">Contraseña</label>
                                <input type="password" required minLength={6} className="gf-input" value={password}
                                    onChange={(e) => setPassword(e.target.value)} data-testid="register-password" />
                            </div>
                            {error && (
                                <div className="text-sm text-[var(--gf-danger)] border-l-2 border-[var(--gf-danger)] pl-3 py-2 bg-red-50" data-testid="register-error">
                                    {error}
                                </div>
                            )}
                            <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="register-submit">
                                {loading ? "Creando cuenta..." : "Crear cuenta y recibir 50 créditos"} <ArrowRight size={16} />
                            </button>
                        </form>

                        <div className="mt-5 flex items-center gap-3">
                            <div className="flex-1 h-px bg-[var(--gf-border)]" />
                            <span className="text-xs text-[var(--gf-text-muted)] uppercase tracking-widest">o</span>
                            <div className="flex-1 h-px bg-[var(--gf-border)]" />
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                                const redirectUrl = window.location.origin + "/dashboard";
                                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                            }}
                            className="mt-4 w-full flex items-center justify-center gap-3 border border-[var(--gf-border)] bg-white hover:bg-[var(--gf-subtle)] py-3 px-4 transition-colors"
                            data-testid="register-google"
                        >
                            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"/></svg>
                            <span className="text-sm font-medium text-[var(--gf-navy)]">Continuar con Google</span>
                        </button>

                        <div className="mt-6 text-center text-sm text-[var(--gf-text-secondary)]">
                            ¿Ya tienes cuenta?{" "}
                            <Link to="/login" className="text-[var(--gf-gold)] font-medium hover:underline" data-testid="to-login">
                                Acceder
                            </Link>
                        </div>
                    </div>
                </motion.div>
            </div>
            <WhatsAppFab />
        </div>
    );
}

const PLAN_LABELS = {
    deposito_100:  "Recarga 100 € créditos",
    deposito_250:  "Recarga 250 € créditos (+10 € bonus)",
    deposito_500:  "Recarga 500 € créditos (+25 € bonus)",
    deposito_1000: "Recarga 1.000 € créditos (+80 € bonus)",
    deposito_custom: "Recarga personalizada",
};
function planLabel(p) { return PLAN_LABELS[p] || p; }
